import {
  createLazySpeechQueue,
  DEFAULT_POLISH_SPEECH_FILTERS,
  PolishSpeechFilters,
  PolishSpeechFragment,
  PolishSpeechQueue,
} from './polishSpeechQueue';

type SpeechBlock = {
  type?: string;
  [key: string]: unknown;
};

const ABBREVIATIONS: Array<[RegExp, string]> = [
  [/\bm\.in\./gi, 'między innymi'],
  [/\bnp\./gi, 'na przykład'],
  [/\btj\./gi, 'to jest'],
  [/\bitd\./gi, 'i tak dalej'],
  [/\bitp\./gi, 'i tym podobne'],
];

/** Removes presentation markup while retaining words suitable for Polish TTS. */
export function cleanPolishSpeechText(text: string): string {
  let result = text
    .replace(/<[^>]*>/g, ' ')
    .replace(/!\[[^\]]*]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/[*_~#>]/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, ' i ')
    .replace(/&lt;/gi, ' mniej niż ')
    .replace(/&gt;/gi, ' więcej niż ')
    .replace(/\+/g, ' plus ')
    .replace(/×/g, ' razy ')
    .replace(/\bVP\b/gi, ' punkty zwycięstwa ')
    .replace(/\bPZ\b/gi, ' punkty zwycięstwa ');

  for (const [pattern, replacement] of ABBREVIATIONS) {
    result = result.replace(pattern, replacement);
  }

  return result.replace(/\s+/g, ' ').trim();
}

/** Selects narrative rulebook content and deliberately ignores images, IDs and links. */
export function extractPolishSpeechParagraphs(
  title: string,
  blocks: readonly SpeechBlock[]
): string[] {
  const paragraphs = [title];
  const text = (value: unknown) => (typeof value === 'string' ? value : undefined);
  const strings = (value: unknown) =>
    Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : [];

  const visit = (block: SpeechBlock) => {
    switch (block.type) {
      case 'heading':
      case 'paragraph':
      case 'warning':
      case 'tip':
      case 'note':
      case 'example':
      case 'commonMistake':
      case 'exception':
      case 'editionWarning':
        if (text(block.title)) paragraphs.push(text(block.title)!);
        if (text(block.text)) paragraphs.push(text(block.text)!);
        break;
      case 'qa':
        if (text(block.question)) paragraphs.push(text(block.question)!);
        if (text(block.answer)) paragraphs.push(text(block.answer)!);
        break;
      case 'step-list':
      case 'bulletList':
      case 'numberedList':
      case 'resolutionOrder':
      case 'checklist':
        if (text(block.title)) paragraphs.push(text(block.title)!);
        paragraphs.push(...strings(block.steps ?? block.items));
        break;
      case 'accordion':
        if (text(block.title)) paragraphs.push(text(block.title)!);
        if (Array.isArray(block.content)) {
          block.content.forEach((content) => {
            if (content && typeof content === 'object') visit(content as SpeechBlock);
          });
        }
        break;
      default:
        break;
    }
  };

  blocks.forEach(visit);
  return paragraphs.map(cleanPolishSpeechText).filter(Boolean);
}

function isEnabledBlock(block: SpeechBlock, filters: PolishSpeechFilters): boolean {
  switch (block.type) {
    case 'example':
      return filters.examples;
    case 'qa':
    case 'faq':
      return filters.faq;
    case 'warning':
    case 'editionWarning':
      return filters.warnings;
    case 'footnote':
      return filters.footnotes;
    default:
      return true;
  }
}

/**
 * Produces stable, individually highlightable fragments. Long paragraphs are split,
 * but unrelated paragraphs are never merged into one active reader item.
 */
export function createPolishSpeechFragments(
  title: string,
  blocks: readonly SpeechBlock[],
  filters: PolishSpeechFilters = DEFAULT_POLISH_SPEECH_FILTERS
): PolishSpeechFragment[] {
  const fragments: PolishSpeechFragment[] = [
    { id: 'title', text: cleanPolishSpeechText(title) },
  ];
  const add = (text: unknown, blockIndex: number, suffix: string, block?: SpeechBlock) => {
    if (typeof text !== 'string') return;
    const cleaned = cleanPolishSpeechText(text);
    if (!cleaned) return;
    const headingId =
      block &&
      block.type === 'heading' &&
      typeof block.id === 'string' &&
      block.id.length > 0
        ? block.id
        : null;
    const idPrefix = headingId ? `heading-${headingId}` : `block-${blockIndex}`;
    chunkPolishSpeech([cleaned]).forEach((chunk, chunkIndex) => {
      fragments.push({
        id: `${idPrefix}-${suffix}-${chunkIndex}`,
        blockId: idPrefix,
        text: chunk,
      });
    });
  };

  blocks.forEach((block, blockIndex) => {
    if (!isEnabledBlock(block, filters)) return;
    switch (block.type) {
      case 'heading':
      case 'paragraph':
      case 'warning':
      case 'tip':
      case 'note':
      case 'example':
      case 'commonMistake':
      case 'exception':
      case 'editionWarning':
      case 'footnote':
        add(block.title, blockIndex, 'title', block);
        add(block.text, blockIndex, 'text', block);
        break;
      case 'qa':
      case 'faq':
        add(block.question, blockIndex, 'question');
        add(block.answer, blockIndex, 'answer');
        break;
      case 'step-list':
      case 'bulletList':
      case 'numberedList':
      case 'resolutionOrder':
      case 'checklist':
        add(block.title, blockIndex, 'title');
        (Array.isArray(block.steps) ? block.steps : Array.isArray(block.items) ? block.items : [])
          .forEach((step, stepIndex) => add(step, blockIndex, `item-${stepIndex}`));
        break;
      case 'accordion':
        add(block.title, blockIndex, 'title');
        if (Array.isArray(block.content)) {
          const nested = createPolishSpeechFragments('', block.content as SpeechBlock[], filters)
            .filter((fragment) => fragment.id !== 'title');
          fragments.push(...nested.map((fragment, nestedIndex) => ({
            ...fragment,
            id: `block-${blockIndex}-nested-${nestedIndex}`,
            blockId: `block-${blockIndex}`,
          })));
        }
        break;
      default:
        break;
    }
  });
  return fragments.filter((fragment) => Boolean(fragment.text));
}

export function preparePolishSpeechQueue(
  title: string,
  blocks: readonly SpeechBlock[],
  filters: PolishSpeechFilters = DEFAULT_POLISH_SPEECH_FILTERS
): PolishSpeechQueue {
  return createLazySpeechQueue(createPolishSpeechFragments(title, blocks, filters));
}

/** Splits at sentence and word boundaries, preserving paragraph order for sequential speech. */
export function chunkPolishSpeech(
  paragraphs: readonly string[],
  maxLength = 360
): string[] {
  const chunks: string[] = [];
  let current = '';
  const append = (piece: string) => {
    if (!piece) return;
    if (!current) {
      current = piece;
      return;
    }
    if (current.length + piece.length + 2 <= maxLength) {
      current += `\n\n${piece}`;
    } else {
      chunks.push(current);
      current = piece;
    }
  };

  for (const paragraph of paragraphs) {
    const sentences = paragraph.match(/[^.!?]+[.!?]+|[^.!?]+$/g) ?? [paragraph];
    for (const sentence of sentences.map((value) => value.trim()).filter(Boolean)) {
      if (sentence.length <= maxLength) {
        append(sentence);
        continue;
      }

      const words = sentence.split(/\s+/);
      let part = '';
      for (const word of words) {
        if (part && part.length + word.length + 1 > maxLength) {
          append(part);
          part = word;
        } else {
          part = part ? `${part} ${word}` : word;
        }
      }
      append(part);
    }
  }
  if (current) chunks.push(current);
  return chunks;
}

export function preparePolishSpeechChunks(
  title: string,
  blocks: readonly SpeechBlock[]
): string[] {
  return chunkPolishSpeech(extractPolishSpeechParagraphs(title, blocks));
}
